// Global packages or components
import { useLoaderData } from '@remix-run/react'
import { resolveBlocks } from '~/.server'
import { getFixedPage } from '~/sanity'
import { getMeta, getSchema, Schema } from '~/utils'
import { Blocks, Breadcrumbs } from '~/components'
import { usePageInfo } from '~/hooks'

// Types
import type {
  MetaFunction,
  LoaderFunction,
  LoaderFunctionArgs,
} from '@remix-run/node'

// Route loader
export const loader: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs) => {
  const env = process.env
  const url = new URL(request.url)

  // Sanity fetch
  const data = await getFixedPage({
    dataset: env?.ENVIRONMENT,
    projectId: env?.SANITY_PROJECT,
    name: 'blogLanding',
  })

  // Page not found
  if (!data) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // Server renders the blog listing, paged by the page param
  const { blocks, listing } = await resolveBlocks({
    blocks: data?.flexibleContent,
    request,
  })

  // Return data
  return Response.json({
    seo: data?.seo,
    blocks,
    listing,
    path: url?.pathname,
  })
}

// Route meta
export const meta: MetaFunction = ({ matches, data, location }) => {
  return getMeta({
    matches,
    data,
    location,
  })
}

// Route view
export default function Index() {
  const { path, blocks, seo } = useLoaderData<typeof loader>()

  usePageInfo({
    pageType: 'Page',
    pageTitle: 'Blog',
  })

  const jsonLd = [
    getSchema({
      type: 'page',
      page: {
        ...seo,
        redirect: path,
      },
    }),
  ]

  return (
    <main>
      <Schema data={jsonLd} />
      <Breadcrumbs path={path} />
      <Blocks blocks={blocks} />
    </main>
  )
}
